"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef } from "react";
import Image from "next/image";
import Text from "@components/atoms/Text";
import styles from "./UserProfile.module.scss";
import { useIsMobile } from "@hooks/use-mobile";
import { usePersistor } from "@lib/redux/PersistorContext";
import { useAppDispatch } from "@lib/redux/hooks";
import { clearProfile } from "@lib/redux/slices/profileSlice";
import assetLoader from "@utils/internalImageLoder";
import { menuItems, defaultUserData } from "@configs/userProfile.config";
import Button from "@components/atoms/Button";
import { useGlobalLoader } from '@hooks/useGlobalLoader';

interface UserProfileProps {
  isOpen: boolean;
  onClose: () => void;
  userData?: {
    name: string;
    phone: string;
    completionPercentage: number;
    avatar?: string;
  };
  isLoggedIn?: boolean;
  onLogout?: () => void;
}

export default function UserProfile({
  isOpen,
  onClose,
  userData,
  isLoggedIn,
  onLogout,
}: UserProfileProps) {
  const router = useRouter();
  const { showLoader, hideLoader } = useGlobalLoader();
  const accessToken = "dummy-access-token-123";
  const refreshToken = "dummy-refresh-token-456";
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const persistor = usePersistor();
  const isMobile = useIsMobile();
  const modalRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    hideLoader();
  }, [pathname, hideLoader]);

  const user = userData || defaultUserData;

  useEffect(() => {
    if (!isMobile && isOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;

        if (modalRef.current && !modalRef.current.contains(target)) {
          if (
            !target.closest("[data-profile-trigger]") &&
            !target.closest(".loggedInIcon")
          ) {
            onClose();
          }
        }
      };

      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isMobile, isOpen, onClose]);

  const handleMenuClick = (href: string) => {
    if (href != pathname) {
      showLoader();
    }
    router.push(href);
    onClose();
  };

  const handleViewProfile = () => {
    router.push(`${process.env.NEXT_PUBLIC_FRONTEND_HOST || ""}/my-profile/`);
    onClose();
  };

  const handleLogout = async () => {
    if (onLogout) {
      onLogout();
    } else {
      if (isLoggedIn) {
        try {
          const basePath = process.env.NEXT_PUBLIC_BASE_TERM
            ? `${process.env.NEXT_PUBLIC_BASE_TERM}`
            : "";
          const response = await fetch(
            `/${basePath}api/auth/logout?refresh=${refreshToken}&redirect=${pathname}`
          );
          const result = await response.json();

          if (!result.success) {
            console.error("Logout API error:", result.error);
          }

          dispatch(clearProfile());

          await persistor.flush();

          router.refresh();
        } catch (error) {
          console.error("Logout failed:", error);
        }
      } else {
        window.location.href = `/api/auth/login?access=${accessToken}&refresh=${refreshToken}&redirect=${pathname}`;
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <div className={styles.mobileContainer}>
        <div className={styles.mobileHeader}>
          <Button onClick={onClose} className={styles.backButton}>
            <Image
              src={`${assetLoader()}/icons/back-icon.svg`}
              alt="Back"
              width={24}
              height={24}
              loading="lazy"
            />
          </Button>
        </div>

        <div className={styles.mobileContent}>
          <div className={styles.userSection}>
            <div className={styles.avatarContainer}>
              <div className={styles.avatar}>
                <Text className={styles.avatarText}>{user.avatar}</Text>
                <div className={styles.completionBadge}>
                  <Text className={styles.completionText}>
                    {user.completionPercentage}%
                  </Text>
                </div>
              </div>
              <div className={styles.userInfo}>
                <Text className={styles.userName}>{user.name}</Text>
                <Text className={styles.userPhone}>{user.phone}</Text>
                <Button
                  onClick={handleViewProfile}
                  className={styles.viewProfileButton}
                >
                  <Text className={styles.viewProfileText}>View profile</Text>
                  <Image
                    src={`${assetLoader()}/icons/Chevron_right_blue.svg`}
                    alt="Arrow"
                    width={20}
                    height={20}
                    loading="lazy"
                  />
                </Button>
              </div>
            </div>
          </div>

          <div className={styles.menuSection}>
            {menuItems.map((item) => (
              <Button
                key={item.id}
                onClick={() => handleMenuClick(item.href)}
                className={styles.menuItem}
              >
                <div className={styles.menuItemLeft}>
                  <Image
                    src={`${assetLoader()}/${item.icon}`}
                    alt={item.label}
                    width={40}
                    height={40}
                    loading="lazy"
                  />
                  <Text className={styles.menuItemText}>{item.label}</Text>
                </div>
                <Image
                  src={`${assetLoader()}/icons/chevron_right_dark.svg`}
                  alt="Arrow"
                  width={20}
                  height={20}
                  loading="lazy"
                />
              </Button>
            ))}
          </div>

          <Button onClick={handleLogout} className={styles.logoutButton}>
            <Text className={styles.logoutText}>Logout</Text>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.desktopModal} ref={modalRef}>
        <div className={styles.userSection}>
          <div className={styles.avatarContainer}>
            <div className={styles.avatar}>
              <Text className={styles.avatarText}>{user.avatar}</Text>
              <div className={styles.completionBadge}>
                <Text className={styles.completionText}>
                  {user.completionPercentage}%
                </Text>
              </div>
            </div>
            <div className={styles.userInfo}>
              <Text className={styles.userName}>{user.name}</Text>
              <Text className={styles.userPhone}>{user.phone}</Text>
              <Button
                onClick={handleViewProfile}
                className={styles.viewProfileButton}
              >
                <Text className={styles.viewProfileText}>View profile</Text>
                <Image
                  src={`${assetLoader()}/icons/Chevron_right_blue.svg`}
                  alt="Arrow"
                  width={20}
                  height={20}
                  loading="lazy"
                />
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.menuSection}>
          {menuItems.map((item) => (
            <Button
              key={item.id}
              onClick={() => handleMenuClick(item.href)}
              className={styles.menuItem}
            >
              <div className={styles.menuItemLeft}>
                <Image
                  src={`${assetLoader()}/${item.icon}`}
                  alt={item.label}
                  width={32}
                  height={32}
                  loading="lazy"
                />
                <Text className={styles.menuItemText}>{item.label}</Text>
              </div>
              <Image
                src={`${assetLoader()}/icons/chevron_right_dark.svg`}
                alt="Arrow"
                width={20}
                height={20}
                loading="lazy"
              />
            </Button>
          ))}
        </div>

        <Button onClick={handleLogout} className={styles.logoutButton}>
          <Text className={styles.logoutText}>Logout</Text>
        </Button>
      </div>
    </>
  );
}
